"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _Radio = _interopRequireDefault(require("./Radio"));
var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _useLabel2 = _interopRequireDefault(require("../../hooks/useLabel"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/**
 * The RadioGroup component offers a convenient way to display multiple Radio components.
 * As they will share the same <strong>name</strong>, the Radio components will be bind together.
 */
var RadioGroup = function RadioGroup(props) {
  var options = props.options,
    checked = props.checked,
    controlledId = props.id,
    controlledName = props.name,
    initialLabel = props.label,
    helper = props.helper,
    inline = props.inline,
    displayAsButton = props.displayAsButton,
    radiosClassName = props.radiosClassName,
    size = props.size,
    _props$color = props.color,
    color = _props$color === void 0 ? 'primary' : _props$color,
    outline = props.outline,
    required = props.required,
    disabled = props.disabled,
    onChange = props.onChange,
    onBlur = props.onBlur,
    onError = props.onError,
    className = props.className;
  var radioGroupRef = _react.default.useRef();
  var id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  var name = controlledName || id;
  var _React$useState = _react.default.useState(checked || false),
    stateChecked = _React$useState[0],
    setChecked = _React$useState[1];
  var _React$useState2 = _react.default.useState(false),
    error = _React$useState2[0],
    setError = _React$useState2[1];
  var _useFormElementValida = (0, _useFormElementValidation.default)(stateChecked, {
      required: required
    }),
    errors = _useFormElementValida[0],
    methods = _useFormElementValida[1];
  var _useLabel = (0, _useLabel2.default)(initialLabel, _extends({}, props, {
      id: id
    }), false),
    label = _useLabel.label;
  _react.default.useEffect(function () {
    setChecked(checked || false);
  }, [checked]);
  _react.default.useEffect(function () {
    setError(errors.length > 0);
  }, [errors]);
  _react.default.useEffect(function () {
    if (typeof onError === 'function') {
      onError({
        name: name,
        errors: errors.length ? errors : []
      });
    }
  }, [errors, onError, name]);
  var handleChange = function handleChange(radioId) {
    methods.onChangeRadioGroup(radioId, options);
    setChecked(radioId);
    if (typeof onChange === 'function') {
      onChange(radioId, name);
    }
  };
  var handleBlur = function handleBlur(event) {
    options.forEach(function () {
      return methods.onChangeRadioGroup(stateChecked, options);
    });
    if (typeof onBlur === 'function') {
      onBlur(event);
    }
  };
  var classes = (0, _clsx.default)('uk-radio-group', displayAsButton && 'uk-radio-group--display-buttons', (helper.length > 0 || error) && 'has-helper', error && 'has-error', inline && 'radio-group--inline', className);
  return /*#__PURE__*/_react.default.createElement("div", {
    className: classes,
    ref: radioGroupRef
  }, label && /*#__PURE__*/_react.default.createElement("h3", {
    className: "uk-radio-group__label",
    id: id + "-radio-label",
    "aria-describedby": (name || id) + "-helper-text"
  }, label), /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-radio-group__radios",
    role: "radiogroup",
    "aria-labelledby": id + "-radio-label"
  }, options.map(function (item, i) {
    return /*#__PURE__*/_react.default.createElement(_Radio.default, {
      key: i,
      name: name,
      id: item.id || id + "_" + i,
      label: item.label,
      checked: item.id === stateChecked,
      "aria-checked": item.id === stateChecked,
      disabled: disabled || item.disabled || false,
      onChange: handleChange,
      onBlur: handleBlur,
      color: color,
      size: size,
      outline: outline,
      value: item.value,
      className: radiosClassName,
      displayAsButton: displayAsButton
    });
  })), (helper.length > 0 || error) && /*#__PURE__*/_react.default.createElement("small", {
    className: "helper",
    id: (name || id) + "-helper-text"
  }, error ? errors : helper));
};
RadioGroup.propTypes = {
  /**
   * Array of Radios props
   */
  options: _propTypes.default.arrayOf(_propTypes.default.shape),
  /**
   * Id of the Radio to set as selected
   */
  checked: _propTypes.default.string,
  /**
   * Prefix for the Radios id (unless it has been set in the options prop)
   */
  id: _propTypes.default.string,
  /**
   * Prefix for the Radios name (unless it has been set in the options prop)
   */
  name: _propTypes.default.string,
  /**
   * Label of the RadioGroup
   */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Layout of the RadioGroup
   */
  inline: _propTypes.default.bool,
  /**
   * Change the display of the RadioGroup
   */
  displayAsButton: _propTypes.default.bool,
  /**
   * ClassName to apply to each Radio
   */
  radiosClassName: _propTypes.default.string,
  /**
   * Sizes of the Radios
   */
  size: _propTypes.default.oneOf(['tiny', 'small', 'default', 'large', 'big']),
  /**
   * Background-color of selected Radio (or dot color if outline)
   */
  color: _propTypes.default.oneOf(['default', 'primary', 'secondary', 'success', 'alert', 'warning', 'white']),
  /**
   * Invert styling colors of the Radios
   */
  outline: _propTypes.default.bool,
  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,
  /**
   * Form-validation: set the RadioGroup as required
   */
  required: _propTypes.default.bool,
  /**
   * Callback function onChange
   */
  onChange: _propTypes.default.func,
  /**
   * Callback function onError
   */
  onError: _propTypes.default.func,
  /**
   * Callback function onBlur
   */
  onBlur: _propTypes.default.func
};
RadioGroup.defaultProps = {
  size: 'default',
  color: 'primary',
  outline: false,
  required: false,
  helper: '',
  radiosClassName: '',
  displayAsButton: false
};
var _default = exports.default = RadioGroup;